// src/modules/app-views/welcome/pages/Init/NextSteps.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState } from 'react';
import { Row, Col, Card, Typography, Button, Tooltip } from 'antd';
import {
  RocketOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ShopOutlined,
  CloseCircleOutlined,
  UserOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import { useNavigate } from 'react-router-dom';
import { useTourStore } from 'stores/tour';
import app from 'configs/appResolver';
import appService from 'services/appService';
import accountService from 'modules/app-views/account/service';
import FirstSubscriptionModal from './modals/FirstSubscriptionModal';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { Title, Text } = Typography;
const sessionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// *******************************************************************************
// STYLED COMPONENTS
// *******************************************************************************
const ActionCard = styled(Card, {
  shouldForwardProp: prop => prop !== 'disableHover',
})`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  transition: all 0.3s ease;
  //cursor: pointer;
  position: relative;

  &:hover {
    transform: ${props => (props.disableHover ? 'none' : 'translateY(-4px)')};
    box-shadow: ${props => (props.disableHover ? 'none' : '0 8px 24px rgba(0, 0, 0, 0.12)')};
  }

  .ant-card-body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    min-height: 260px;
  }
`;

const IconWrapper = styled.div`
  font-size: 48px;
  color: ${props => props.color || props.theme?.colorPrimary || '#1890ff'};
`;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const NextSteps = ({ onStartTour }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedUser = useZustand('auth', 'user');
  const isTourOpen = useTourStore(s => s.isOpen);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleNavigate = path => {
    navigate(path);
  };

  const handleCloseActionCards = async () => {
    if (!validateNextStepsClose({ storedUser, appService })) return;
    await appService.updateModulePreferences({
      category: 'tour',
      data: { showNextStepsSession: false },
      moduleId: 'welcome',
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  const actionCards = [
    {
      key: 'explore_the_system',
      icon: <RocketOutlined />,
      buttonIcon: <RocketOutlined />,
      buttonTitle: translateX('start'),
      title: translateX('understanding_the_system'),
      description: translateX('explore_your_system'),
      color: app.baseColor,
      action: async () => onStartTour?.(),
      completed: accountService.hasNextStep('explore_the_system'),
      disabled: false,
    },
    {
      key: 'complete_your_profile',
      icon: <UserOutlined />,
      buttonIcon: <LinkOutlined />,
      buttonTitle: translateX('access_page'),
      title: translateX('complete_your_profile'),
      description: translateX('finish_your_personal_registration'),
      color: app.baseColor,
      action: () => handleNavigate(`/app/account/${storedUser?._id}?tab=my_profile`),
      completed: accountService.hasNextStep('complete_your_profile'),
      // disabled:
      //   !accountService.hasNextStep('explore_the_system') &&
      //   !accountService.hasNextStep('complete_your_profile'),
    },
    // {
    //   key: 'create_your_first_subscription',
    //   icon: <ShopOutlined />,
    //   buttonIcon: <LinkOutlined />,
    //   buttonTitle: translateX('access_page'),
    //   title: translateX('create_your_organization'),
    //   description: translateX('active_your_subscription_register_your_company'),
    //   color: app.baseColor,
    //   action: () => setShowSubscriptionModal(true),
    //   completed: accountService.hasNextStep('create_your_first_subscription'),
    //   // disabled:
    //   //   !accountService.hasNextStep('explore_the_system') &&
    //   //   !accountService.hasNextStep('create_your_first_subscription'),
    // },
    // {
    //   key: 'invite_your_team',
    //   icon: <TeamOutlined />,
    //   buttonIcon: <LinkOutlined />,
    //   buttonTitle: translateX('access_page'),
    //   title: translateX('invite_team'),
    //   description: translateX('add_members_collaborators'),
    //   color: app.baseColor,
    //   action: () => handleNavigate(`/app/account/${storedUser?._id}?tab=team`),
    //   completed: accountService.hasNextStep('invite_your_team'),
    //   disabled:
    //     !accountService.hasNextStep('explore_the_system') ||
    //     !accountService.hasNextStep('create_your_first_subscription'),
    //   // disabled: !storedSubscription,
    // },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <motion.div
      variants={sessionVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={{ duration: 0.3 }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={3} style={{ margin: 0, padding: '0 4px' }}>
          {translateX('next_steps')}
        </Title>
        <Tooltip title={translateX('hide_this_session')}>
          <span id="tour-hide-cards-button">
            <CloseCircleOutlined
              onClick={handleCloseActionCards}
              style={{
                fontSize: 24,
                color: '#999',
                cursor: 'pointer',
                transition: 'color 0.3s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#333')}
              onMouseLeave={e => (e.currentTarget.style.color = '#999')}
            />
          </span>
        </Tooltip>
      </div>
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {actionCards.map((card, index) => (
          <Col xs={24} sm={8} key={card.key} style={{ display: 'flex' }}>
            <motion.div
              id={`tour-card-${card.key}`}
              whileHover={card.disabled || isTourOpen ? {} : { scale: 1.02 }}
              whileTap={card.disabled || isTourOpen ? {} : { scale: 0.98 }}
              style={{ width: '100%', display: 'flex' }}
            >
              <ActionCard
                disableHover={isTourOpen}
                // onClick={card.disabled ? undefined : () => handleCardAction(card, index)}
                style={{
                  pointerEvents: isTourOpen ? 'none' : 'auto',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Icon and Check */}
                  <div style={{ position: 'relative' }}>
                    <IconWrapper color={card.color}>{card.icon}</IconWrapper>
                    {card.completed && (
                      <CheckCircleOutlined
                        style={{
                          fontSize: 24,
                          color: '#52c41a',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                        }}
                      />
                    )}
                  </div>

                  {/* Title and Description */}
                  <div style={{ marginTop: 16 }}>
                    <Title level={4} style={{ marginBottom: 8 }}>
                      {card.title}
                    </Title>
                    <Text type="secondary">{card.description}</Text>
                  </div>

                  {/* Flexible spacer */}
                  <div style={{ flex: 1 }} />

                  {/* Button */}
                  <div style={{ marginTop: 16 }}>
                    <Button
                      type="primary"
                      block
                      icon={card.buttonIcon}
                      disabled={card.disabled}
                      onClick={card.action}
                    >
                      {card.buttonTitle}
                    </Button>
                  </div>
                </div>
              </ActionCard>
            </motion.div>
          </Col>
        ))}
      </Row>

      {showSubscriptionModal && (
        <FirstSubscriptionModal
          show={showSubscriptionModal}
          setShow={setShowSubscriptionModal}
        />
      )}
    </motion.div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default NextSteps;

// *******************************************************************************
// HELPERS
// *******************************************************************************
export const validateNextStepsClose = ({ appService }) => {
  // Check directly against user's completed steps (source of truth)
  const storedUser = useZustand('auth', 'user', false);
  const allSteps = app.WELCOME_NEXT_STEPS || [];
  const completedSteps = storedUser?.welcomeNextSteps || [];
  const allDone = allSteps.length > 0 && allSteps.every(s => completedSteps.includes(s));

  // function temporarily disabled, can close in the any way.
  // if (!allDone) {
  //   appService.notification({
  //     key: 'action_not_allowed',
  //     title: translateX('action_not_allowed'),
  //     description: translateX('complete_all_steps_to_hide'),
  //   });
  //   return false;
  // }

  // Sync the "done" flag in module preferences if not already set
  const storedDone = appService.getModulePreferences({
    category: 'tour',
    moduleId: 'welcome',
    key: 'done',
  });

  if (!storedDone) {
    appService.updateModulePreferences({
      category: 'tour',
      data: { done: true },
      moduleId: 'welcome',
      ignoreSubscription: true,
    });
  }

  return true;
};
